/* Printofy Block Tee preview — independent front and back artwork. */
(() => {
  'use strict';
  if (window.__printofyPreviewV2) return;
  window.__printofyPreviewV2 = true;
  const initialized = new WeakSet();
  let instanceCount = 0;
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  function init(root) {
    if (initialized.has(root)) return;
    const q = name => root.querySelector(`[data-pf-${name}]`);
    const stage = q('stage'), shirt = q('shirt'), logo = q('logo');
    const logoImage = q('logo-image'), file = q('file'), colour = q('colour');
    const size = q('size'), save = q('save'), error = q('error'), feedback = q('feedback');
    if (![stage, shirt, logo, logoImage, file, colour, size, save, error, feedback, q('images')].every(Boolean)) return;
    let images;
    try {
      images = JSON.parse(q('images').textContent).images;
      if (!Array.isArray(images) || !images.length || images.some(i => !i.src || !i.label)) throw new Error();
    } catch (_) {
      error.hidden = false;
      error.textContent = 'The preview images could not be read. Please contact Printofy.';
      return;
    }
    initialized.add(root);
    const viewButtons = [...root.querySelectorAll('[data-pf-view]')];
    const placements = [...root.querySelectorAll('[data-pf-placement]')].filter(button => {
      if (button.dataset.pfPlacement === 'centre') { button.remove(); return false; }
      return true;
    });
    const baseId = 'printofy-block-tee-preview';
    root.id = document.getElementById(baseId) ? `${baseId}-${++instanceCount}` : baseId;
    root.setAttribute('tabindex', '-1');
    const states = {
      front: { x: .64, y: .30, width: 14, placement: 'chest', design: null, uploading: false, uploadId: 0 },
      back: { x: .50, y: .33, width: 30, placement: 'top', design: null, uploading: false, uploadId: 0 }
    };
    let view = 'front', garment = null;
    let loadId = 0, garmentLoading = false, saving = false;
    let exportable = false, dragging = null;
    const state = () => states[view];
    const message = text => { error.textContent = text; error.hidden = !text; };
    const status = text => { feedback.textContent = text; };
    const syncSave = () => { save.disabled = !state().design || !garment || garmentLoading || state().uploading || saving; };
    // Avoid theme image rules that stretch the stage and misalign logo coordinates.
    shirt.style.cssText = 'display:block;width:100%;height:auto;min-height:0;max-width:none;';
    shirt.removeAttribute('srcset');
    logo.setAttribute('aria-label', 'Logo position. Use arrow keys to move the logo.');
    stage.setAttribute('aria-label', 'Garment preview');
    colour.setAttribute('aria-label', 'Preview colour');
    file.setAttribute('aria-label', 'Upload your logo');
    colour.replaceChildren(...images.map((item, index) => new Option(item.label, String(index))));
    const help = root.querySelector('.pf-help');
    if (help) help.textContent = 'Different designs? Upload one for each side. PNG, JPG or WebP · Up to 10 MB each. Transparent PNG works best.';
    const orderNote = root.querySelector('.pf-order-note');
    if (orderNote) orderNote.textContent = 'Preview only. Please upload both original artwork files separately when ordering.';
    status('Loading tee preview…');

    function constrain() {
      const s = state(), design = s.design;
      const r = stage.getBoundingClientRect();
      if (!design || !r.width || !r.height) return;
      const halfW = s.width / 200;
      const halfH = halfW * r.width / r.height * design.naturalHeight / design.naturalWidth;
      s.x = clamp(s.x, halfW, 1 - halfW);
      s.y = halfH >= .5 ? .5 : clamp(s.y, halfH, 1 - halfH);
    }
    function render() {
      constrain();
      const s = state(), design = s.design;
      if (design) {
        if (logoImage.getAttribute('src') !== design.src) logoImage.src = design.src;
      } else {
        logoImage.removeAttribute('src');
      }
      if (q('upload-label')) q('upload-label').textContent = `${design ? 'Change' : 'Upload'} ${view} logo`;
      file.setAttribute('aria-label', `Upload ${view} logo`);
      if (q('remove')) q('remove').textContent = `Remove ${view} logo`;
      save.textContent = `Save ${view} PNG`;
      logoImage.alt = `Your uploaded ${view} design`;
      logo.style.left = `${s.x * 100}%`;
      logo.style.top = `${s.y * 100}%`;
      logo.style.width = `${s.width}%`;
      size.value = String(s.width);
      logo.hidden = !design;
      if (q('edit')) q('edit').hidden = !design;
      if (q('hint')) q('hint').hidden = !design;
      if (q('back-note')) q('back-note').hidden = view !== 'back';
      placements.forEach(button => {
        const p = button.dataset.pfPlacement;
        button.hidden = view === 'back' ? p !== 'top' : p === 'top';
        button.setAttribute('aria-pressed', String(p === s.placement));
      });
      viewButtons.forEach(button => {
        const side = button.dataset.pfView;
        button.setAttribute('aria-pressed', String(side === view));
        button.textContent = `${side === 'front' ? 'Front' : 'Back'} preview${states[side].design ? ' · Added' : ''}`;
      });
      syncSave();
    }
    function loadImage(src, cors) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        if (cors) img.crossOrigin = 'anonymous';
        const timer = setTimeout(() => { img.onload = img.onerror = null; reject(new Error('Image timed out')); }, 20000);
        img.onload = () => { clearTimeout(timer); resolve(img); };
        img.onerror = () => { clearTimeout(timer); reject(new Error('Image could not load')); };
        img.src = src;
      });
    }
    async function loadGarment() {
      const id = ++loadId;
      const item = images[Number(colour.value)] || images[0];
      const selectedView = view;
      const src = selectedView === 'back' ? item.backSrc : item.src;
      garmentLoading = true;
      garment = null;
      exportable = false;
      message('');
      status('Loading tee preview…');
      // Hide the previous colour/view so failed requests never display a misleading preview.
      shirt.style.visibility = 'hidden';
      stage.style.aspectRatio = '1 / 1';
      render();
      try {
        if (!src) throw new Error('No image');
        let img, canExport = true;
        try { img = await loadImage(src, true); }
        catch (_) {
          if (id !== loadId || !root.isConnected) return;
          img = await loadImage(src, false);
          canExport = false;
        }
        if (id !== loadId || !root.isConnected) return;
        garment = img;
        exportable = canExport;
        if (canExport) shirt.crossOrigin = 'anonymous';
        else shirt.removeAttribute('crossorigin');
        shirt.src = src;
        shirt.alt = `${item.label} AS Colour Block Tee, ${selectedView} view`;
        stage.style.aspectRatio = `${img.naturalWidth} / ${img.naturalHeight}`;
        shirt.style.visibility = 'visible';
        status(state().uploading ? `Loading your ${view} logo…` : state().design ? `${view === 'front' ? 'Front' : 'Back'} preview ready. Drag your logo to adjust its position.` : `Upload your ${view} logo. Each side can have a different design.`);
      } catch (_) {
        if (id !== loadId || !root.isConnected) return;
        message('This tee image could not load. Please try another colour or reload the page.');
        status('');
      } finally {
        if (id === loadId && root.isConnected) { garmentLoading = false; render(); }
      }
    }
    file.addEventListener('change', async () => {
      const selected = file.files && file.files[0];
      file.value = ''; // Allow selecting the same file again.
      if (!selected) return;
      // Bind this upload to the side selected when the file was chosen.
      // Switching sides while an image loads must never put it on the other side.
      const selectedView = view, target = state();
      const id = ++target.uploadId;
      target.uploading = false;
      message('');
      syncSave();
      const types = ['image/png', 'image/jpeg', 'image/webp'];
      if (!types.includes(selected.type) && !(selected.type === '' && /\.(png|jpe?g|webp)$/i.test(selected.name))) {
        message('Please upload a PNG, JPG or WebP image. PDF, AI and EPS files cannot be previewed here.'); return;
      }
      if (selected.size > 10 * 1024 * 1024) { message('Please choose a logo smaller than 10 MB.'); return; }
      target.uploading = true;
      syncSave();
      status(`Loading your ${selectedView} logo…`);
      const url = URL.createObjectURL(selected);
      try {
        const img = await loadImage(url, false);
        if (id !== target.uploadId || !root.isConnected) return;
        if (!img.naturalWidth || !img.naturalHeight) throw new Error();
        const ratio = Math.min(1, 2048 / Math.max(img.naturalWidth, img.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.naturalWidth * ratio));
        canvas.height = Math.max(1, Math.round(img.naturalHeight * ratio));
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error();
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const data = canvas.toDataURL('image/png');
        const prepared = await loadImage(data, false);
        if (id !== target.uploadId || !root.isConnected) return;
        target.design = prepared;
        if (view === selectedView) {
          status(`${selectedView === 'front' ? 'Front' : 'Back'} logo loaded. Drag it to adjust its position.`);
        }
        render();
      } catch (_) {
        if (id === target.uploadId && view === selectedView) {
          message('We could not open that image. Please try another PNG, JPG or WebP.'); status('');
        }
      } finally {
        URL.revokeObjectURL(url);
        if (id === target.uploadId) { target.uploading = false; syncSave(); }
      }
    });
    colour.addEventListener('change', loadGarment);
    viewButtons.forEach(button => button.addEventListener('click', () => {
      const next = button.dataset.pfView;
      if (!states[next] || next === view) return;
      dragging = null;
      view = next;
      loadGarment();
    }));
    placements.forEach(button => button.addEventListener('click', () => {
      const p = button.dataset.pfPlacement;
      if ((view === 'front' && p !== 'chest') || (view === 'back' && p !== 'top')) return;
      Object.assign(state(), p === 'chest' ? { x: .64, y: .30, width: 14, placement: 'chest' } :
        { x: .5, y: .33, width: 30, placement: 'top' });
      render();
    }));
    size.addEventListener('input', () => {
      state().width = clamp(Number(size.value) || 14, 6, 55);
      render();
    });
    if (q('remove')) q('remove').addEventListener('click', () => {
      const target = state();
      ++target.uploadId;
      target.uploading = false;
      target.design = null;
      dragging = null;
      message(''); status(`${view === 'front' ? 'Front' : 'Back'} logo removed.`); render();
    });
    logo.addEventListener('pointerdown', event => {
      if (!state().design || garmentLoading || (event.pointerType === 'mouse' && event.button !== 0)) return;
      event.preventDefault();
      const rect = stage.getBoundingClientRect();
      dragging = { id: event.pointerId, dx: (event.clientX - rect.left) / rect.width - state().x,
        dy: (event.clientY - rect.top) / rect.height - state().y };
      logo.setPointerCapture(event.pointerId);
      logo.focus({ preventScroll: true });
    });
    logo.addEventListener('pointermove', event => {
      if (!dragging || dragging.id !== event.pointerId) return;
      event.preventDefault();
      const r = stage.getBoundingClientRect();
      state().x = (event.clientX - r.left) / r.width - dragging.dx;
      state().y = (event.clientY - r.top) / r.height - dragging.dy;
      render();
    });
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(name => logo.addEventListener(name, () => { dragging = null; }));
    logo.addEventListener('keydown', event => {
      if (!state().design || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
      event.preventDefault();
      const step = event.shiftKey ? .02 : .005;
      if (event.key === 'ArrowLeft') state().x -= step;
      if (event.key === 'ArrowRight') state().x += step;
      if (event.key === 'ArrowUp') state().y -= step;
      if (event.key === 'ArrowDown') state().y += step;
      render();
    });
    save.addEventListener('click', async () => {
      if (save.disabled) return;
      message('');
      if (!exportable) { message('Your preview works, but this tee image does not allow PNG downloads. You can take a screenshot instead.'); return; }
      const design = state().design, savedView = view;
      saving = true; syncSave(); status('Preparing PNG…');
      try {
        const canvas = document.createElement('canvas');
        const max = 2000;
        const scale = Math.min(1, max / Math.max(garment.naturalWidth, garment.naturalHeight));
        canvas.width = Math.round(garment.naturalWidth * scale);
        canvas.height = Math.round(garment.naturalHeight * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error();
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(garment, 0, 0, canvas.width, canvas.height);
        const s = state(), width = s.width / 100 * canvas.width;
        const height = width * design.naturalHeight / design.naturalWidth;
        ctx.drawImage(design, s.x * canvas.width - width / 2, s.y * canvas.height - height / 2, width, height);
        const filename = `Printofy-${images[Number(colour.value)].label.replace(/[^a-z0-9]+/ig, '-')}-${view}-preview.png`;
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        if (!blob) throw new Error();
        const url = URL.createObjectURL(blob), link = document.createElement('a');
        link.href = url; link.download = filename;
        document.body.append(link); link.click(); link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        if (view === savedView) status('PNG ready. If it opens in a new tab on mobile, press and hold the image to save it.');
      } catch (_) {
        message('The PNG could not be saved. Please try again, or take a screenshot of your preview.'); status('');
      } finally { saving = false; syncSave(); }
    });
    // The garment maintains a fixed ratio, so percentage positioning also works on resize.
    render();
    loadGarment();
  }
  function scan(scope) {
    if (scope.matches && scope.matches('[data-printofy-preview]')) init(scope);
    if (scope.querySelectorAll) scope.querySelectorAll('[data-printofy-preview]').forEach(init);
  }
  function syncJumpLinks() {
    const target = document.getElementById('printofy-block-tee-preview');
    document.querySelectorAll('[data-pf-jump]').forEach(link => {
      link.hidden = !target;
      link.setAttribute('href', '#printofy-block-tee-preview');
    });
  }
  function start() {
    const style = document.createElement('style');
    style.id = 'printofy-preview-polish';
    style.textContent = PREVIEW_CSS;
    document.head.append(style);
    scan(document);
    syncJumpLinks();
    document.addEventListener('click', event => {
      const link = event.target.closest && event.target.closest('[data-pf-jump]');
      if (!link) return;
      const target = document.getElementById('printofy-block-tee-preview');
      if (!target) return;
      event.preventDefault();
      // Expand a native description accordion, or activate an accessible description tab.
      let ancestor = target.parentElement;
      while (ancestor) {
        if (ancestor.tagName === 'DETAILS') ancestor.open = true;
        if (ancestor.getAttribute('role') === 'tabpanel' && ancestor.id) {
          const tab = [...document.querySelectorAll('[role="tab"][aria-controls]')]
            .find(button => button.getAttribute('aria-controls') === ancestor.id);
          if (tab) tab.click();
        }
        ancestor = ancestor.parentElement;
      }
      requestAnimationFrame(() => {
        target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        target.focus({ preventScroll: true });
      });
    });
    const observer = new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(node => {
        if (node.nodeType !== 1) return;
        scan(node);
        const parentPreview = node.closest('[data-printofy-preview]');
        if (parentPreview) init(parentPreview);
      }));
      syncJumpLinks();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', event => { scan(event.target); syncJumpLinks(); });
  }
  const PREVIEW_CSS = `
.pf-preview[data-printofy-preview]{--pf-gold:#f6c945;--pf-dark:#17191b;--pf-line:#dfe2e4;max-width:100%;padding:0;margin:28px 0;border:1px solid #dfe2e4;border-radius:18px;background:#fff;box-shadow:0 8px 30px #17191b0a;overflow:hidden;scroll-margin-top:140px;color:#17191b}
.pf-preview[data-printofy-preview] .pf-preview-heading{background:#17191b;padding:23px 25px 22px;border-bottom:4px solid #f6c945}
.pf-preview[data-printofy-preview] .pf-kicker{font-size:12px;letter-spacing:1.2px;padding:5px 9px;margin-bottom:10px;border-radius:5px;color:#17191b;background:#f6c945}
.pf-preview[data-printofy-preview] h2{font-size:34px;color:#fff;line-height:1.08;margin:0;letter-spacing:.25px}
.pf-preview[data-printofy-preview] .pf-reassurance{color:#e2e4e6;border:0;padding:0;margin-top:10px;font-size:14px;line-height:1.55;max-width:650px}
.pf-preview[data-printofy-preview] .pf-preview-grid{padding:22px;gap:22px;grid-template-columns:minmax(0,1.25fr) minmax(240px,1fr)}
.pf-preview[data-printofy-preview] .pf-preview-photo{min-width:0;background:#f7f8f8;border:1px solid #e7e9e9;border-radius:13px;padding:12px}
.pf-preview[data-printofy-preview] .pf-views{background:#e9ebec;padding:4px;gap:4px;border-radius:10px;margin:0 0 12px}
.pf-preview[data-printofy-preview] .pf-views button{min-width:0;min-height:43px;padding:10px 5px;font-size:14px;background:transparent;border:0;border-radius:7px;color:#41464a;box-shadow:none;white-space:normal}
.pf-preview[data-printofy-preview] .pf-views button[aria-pressed=true]{background:#17191b;color:#fff;box-shadow:0 2px 5px #0002}
.pf-preview[data-printofy-preview] .pf-stage{border:1px solid #eceeef;border-radius:9px;background:#fff}
.pf-preview[data-printofy-preview] .pf-preview-controls{padding:16px;background:#f7f8f8;border:1px solid #e7e9e9;border-radius:13px;min-width:0}
.pf-preview[data-printofy-preview] .pf-label{font-size:14px;gap:7px;margin-bottom:14px}
.pf-preview[data-printofy-preview] .pf-label select{height:46px;min-height:46px;border-radius:8px;padding:9px 12px;color:#17191b;background:#fff;border:1px solid #cfd4d6}
.pf-preview[data-printofy-preview] .pf-upload{min-height:49px;font-size:16px;border-radius:9px;background:#f6c945;color:#17191b;border:1px solid #dfb539;box-shadow:0 2px 0 #dfb539;transition:background .15s,color .15s}
.pf-preview[data-printofy-preview] .pf-upload:hover{background:#17191b;color:#f6c945;border-color:#17191b}
.pf-preview[data-printofy-preview] .pf-help{font-size:12px;line-height:1.6;color:#60676b;margin:11px 0 16px}
.pf-preview[data-printofy-preview] .pf-label-text{font-size:13px;color:#4e5559;margin-bottom:7px}
.pf-preview[data-printofy-preview] .pf-placements{margin-bottom:16px;gap:0}
.pf-preview[data-printofy-preview] .pf-placements button{flex:0 1 auto;min-width:125px;padding:9px 14px;font-size:14px;border-radius:7px}
.pf-preview[data-printofy-preview] .pf-placements button[aria-pressed=true]{background:#fff4cd;border:1px solid #dfb539;color:#17191b}
.pf-preview[data-printofy-preview] .pf-size input{accent-color:#17191b;margin:2px 0;height:25px}
.pf-preview[data-printofy-preview] .pf-save{min-height:46px;border-radius:8px;font-size:15px!important;padding:10px 14px;background:#17191b;color:#fff}
.pf-preview[data-printofy-preview] .pf-save:hover:not(:disabled){background:#34383b}
.pf-preview[data-printofy-preview] .pf-remove{width:auto;min-height:38px;margin:5px auto 0;padding:8px 12px;text-decoration:underline;text-underline-offset:3px;color:#555d62;font-size:12px!important}
.pf-preview[data-printofy-preview] .pf-order-note{border-color:#dfe2e4;margin-top:12px!important;padding-top:11px;font-size:12px;line-height:1.55;color:#60676b}
.pf-preview[data-printofy-preview] .pf-drag-hint,.pf-preview[data-printofy-preview] .pf-back-note{font-size:12px;line-height:1.5;margin-top:9px!important;color:#60676b}
.pf-preview[data-printofy-preview] .pf-error{color:#573e0e;background:#fff2c8;font-size:13px}
.pf-preview[data-printofy-preview] .pf-feedback{font-size:12px;line-height:1.5;color:#454e53}
.pf-preview[data-printofy-preview] .pf-logo:hover,.pf-preview[data-printofy-preview] .pf-logo:focus-visible{outline:1px dashed #d1a520;outline-offset:4px}
.pf-preview[data-printofy-preview]:focus{outline:none}
.pf-preview[data-printofy-preview]:focus-visible{outline:3px solid #c69b1f;outline-offset:4px}
.pf-preview-jump{display:flex;align-items:center;justify-content:center;gap:9px;width:100%;padding:13px 16px;margin:10px 0;font:700 15px/1.35 Barlow,Arial,sans-serif;background:#fff8e2;color:#17191b!important;border:1px solid #d6b451;border-radius:9px;text-decoration:none!important;cursor:pointer;scroll-margin-top:120px}
.pf-preview-jump:hover{background:#f6c945;color:#17191b!important}
.pf-preview-jump:focus-visible{outline:3px solid #17191b;outline-offset:3px}
.pf-preview-jump[hidden]{display:none!important}
@media(max-width:760px){.pf-preview[data-printofy-preview] .pf-preview-grid{grid-template-columns:1fr;gap:14px;padding:15px}.pf-preview[data-printofy-preview] .pf-preview-heading{padding:20px}.pf-preview[data-printofy-preview] h2{font-size:31px}.pf-preview[data-printofy-preview] .pf-stage{max-width:460px}.pf-preview[data-printofy-preview] .pf-preview-controls{padding:16px}.pf-preview[data-printofy-preview] .pf-reassurance{font-size:13px}}
@media(prefers-reduced-motion:reduce){.pf-preview[data-printofy-preview] *, .pf-preview-jump{transition:none!important}}
`;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();